import React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fi } from 'date-fns/locale';

import styles from '../Billing/styles/MyBillsTable.module.scss';

import Box from '../Common/Box';
import ChargingHistoryTableRow from './ChargingHistoryTableRow';

// Types.
import { TransactionHistoryEventUI } from '../../types/evCharging';
// Utils.
import { useWindowSize } from '../Layout/Header';
import { capitalizeFirstLetter } from '../../utils/consumption';
import { roundNumber, setPriceFormat } from '../../utils/billing';

type tableSorterConfig = {
  key: keyof TransactionHistoryEventUI;
  direction: string;
};
type tableSorter = {
  items: TransactionHistoryEventUI[];
  requestSort: (key: any) => void;
  sortConfig?: tableSorterConfig;
};

// https://www.smashingmagazine.com/2020/03/sortable-tables-react/

const useSortableData = (items: TransactionHistoryEventUI[]): tableSorter => {
  const [sortConfig, setSortConfig] = React.useState<tableSorterConfig>();

  const sortedItems = React.useMemo((): TransactionHistoryEventUI[] => {
    const sortableItems = [...items];
    if (sortConfig !== undefined) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key: keyof TransactionHistoryEventUI): void => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
};

type ChargingHistoryTableType = {
  charges: TransactionHistoryEventUI[];
};

const ChargingHistoryTable: React.FunctionComponent<ChargingHistoryTableType> = (props: ChargingHistoryTableType) => {
  const { t } = useTranslation();

  const { charges } = props;
  const { items, requestSort, sortConfig } = useSortableData(
    charges.filter((c) => c.evseTransactionId !== '').sort((a, b) => (a.startTimeUTC < b.startTimeUTC ? 1 : -1)),
  );

  const getClassNamesFor = (name: keyof TransactionHistoryEventUI): string | undefined => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  const windowWidth = useWindowSize()[0];
  const isMobile = windowWidth < 768;

  const sections = React.useMemo(() => {
    if (!items) {
      return [];
    }

    const chargingData: { [key: string]: TransactionHistoryEventUI[] } = {};

    for (const chargingHistoryEntry of items) {
      const date = new Date(chargingHistoryEntry.startTimeUTC);
      const dateKey = capitalizeFirstLetter(format(date, 'LLLL yyyy', { locale: fi }));
      if (!chargingData[dateKey]) {
        chargingData[dateKey] = [];
      }

      chargingData[dateKey].push(chargingHistoryEntry);
    }

    return Object.keys(chargingData).map((dateKey) => ({
      title: dateKey,
      data: chargingData[dateKey],
      count: chargingData[dateKey].length,
      totalCost: chargingData[dateKey].reduce((acc, entry) => acc + (entry.totalCostInclVat || 0), 0),
    }));
  }, [items]);

  return (
    <Box noStyles={isMobile}>
      <div className={`table-responsive ${styles.tableWrapper}`}>
        <div className={styles.table}>
          <span className="sr-only">{t(`billing:billing:sortHeading`)}</span>
          <div className={`${styles.header} ${styles.boxHeader}`}>
            <div>
              <button onClick={(): void => requestSort('groupName')} className={sortConfig && sortConfig.key === 'groupName' ? styles.active : ''}>
                {t('evCharging:common.site')}
                <span className={styles[getClassNamesFor('groupName') ?? '']} />
              </button>
            </div>
            <div>
              <button
                onClick={(): void => requestSort('consumptionKWH')}
                className={sortConfig && sortConfig.key === 'consumptionKWH' ? styles.active : ''}
              >
                {t('evCharging:history.tableAmount')}
                <span className={styles[getClassNamesFor('consumptionKWH') ?? '']} />
              </button>
            </div>
            <div>
              <button
                onClick={(): void => requestSort('startTimeUTC')}
                className={sortConfig && sortConfig.key === 'startTimeUTC' ? styles.active : ''}
              >
                {t('evCharging:history.tableDate')}
                <span className={styles[getClassNamesFor('startTimeUTC') ?? '']} />
              </button>
            </div>
            <div>
              <button
                onClick={(): void => requestSort('totalCostInclVat')}
                className={sortConfig && sortConfig.key === 'totalCostInclVat' ? styles.active : ''}
              >
                {t('evCharging:history.tablePrice')}
                <span className={styles[getClassNamesFor('totalCostInclVat') ?? '']} />
              </button>
            </div>
            <div>
              <button>{t('evCharging:history.tableMoreInfo')}</button>
            </div>
          </div>
          {sections.map((section) => {
            return (
              <React.Fragment key={section.title}>
                {(sortConfig === undefined || (sortConfig.key === 'startTimeUTC' && sortConfig.direction === 'descending')) && (
                  <h4 className={styles.sectionTitle}>
                    {section.title} {t('evCharging:history.totalShort')} {section.count} {t('evCharging:history.totalUnit')}{' '}
                    {setPriceFormat(roundNumber(section.totalCost))} €
                  </h4>
                )}
                {section.data.map((charge) => (
                  <ChargingHistoryTableRow key={charge.evseTransactionId} charge={charge} />
                ))}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </Box>
  );
};

export default ChargingHistoryTable;
